import * as React from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useTable, useBlockLayout, useFlexLayout, useResizeColumns, usePagination } from 'react-table-new';
import { Result, Pagination } from 'antd';

import * as Decorators from 'decorators/text';
import * as Views from 'decorators/views';
import { FormattedMessageDynamic } from 'vendys-commons/utils';
import messages from './messages';
import { Container } from './styles';
import TableRow from './TableRow';

const DecoratorsIn = { ...Decorators, ...Views };

interface PageProps {
  page: number;
  pageRow: number;
  totalCount: number;
}

interface TableProps {
  boolean?: boolean;
  data: [];
  style?;
  cellProps?;
  columns?: Array<any>;
  string?: string;
  totalCount?: number;
  // eslint-disable-next-line no-unused-vars
  onChange?: (d: object) => void;
  type?: 'small' | 'medium' | 'large';
  onFunc?: () => void;
  pageInfo: PageProps;
  // eslint-disable-next-line no-unused-vars
  onRowClick?: (e) => void;
  // eslint-disable-next-line no-unused-vars
  confirm?: (t: any, row: any) => void;
  // eslint-disable-next-line no-unused-vars
  cancel?: (t: any, row: any) => void;
  isAutoColumns?: boolean;
  isResizeColumn?: boolean;
}
export const makeColumnsFromData = (d, customColumns = []) => {
  let columns = [];
  if (!d) return columns;
  if (d && d.length > 0) {
    const row = d[0];
    columns = Object.keys(row);
  }

  columns = columns.filter((row) => row[0] !== '_');
  return columns.map((column) => {
    const cc = customColumns.find((c) => c.accessor === column);
    if (cc) {
      return cc;
    }
    return {
      Header: column,
      accessor: column
    };
  });
};

const Table: React.FC<TableProps> = ({
  data,
  style,
  columns,
  currentPage,
  onChange = () => {},
  cellProps = {},
  onRowClick,
  /* onRowClick=(e)=>{}, */ confirm = () => {},
  cancel = () => {},
  pageInfo,
  isAutoColumns = true,
  isResizeColumn = false
}) => {
  const _pageCount = React.useMemo(() => {
    if (!pageInfo) return 0;
    const { page = 0, pageRow = 0, totalCount = 0 } = pageInfo;

    if (totalCount === 0 && pageRow === 0) {
      return undefined;
    }
    return parseInt(`${totalCount / pageRow}`, 10);
  }, [pageInfo]);

  const [_pageRow, setPageRow] = useState(pageInfo?.pageRow || 10);

  // pageInfo가 변경될 때 _pageRow도 업데이트
  React.useEffect(() => {
    if (pageInfo?.pageRow) {
      setPageRow(pageInfo.pageRow);
    }
  }, [pageInfo?.pageRow]);
  const defaultColumn = React.useMemo(
    () => ({
      // When using the useFlexLayout:
      minWidth: 30, // minWidth is only used as a limit for resizing
      width: 150, // width is used for both the flex-basis and flex-grow
      maxWidth: 200 // maxWidth is only used as a limit for resizing
    }),
    []
  );

  React.useEffect(() => {
    if (!pageInfo) return;
    const { pageRow = 0 } = pageInfo;
    if (pageRow) {
      setPageRow(pageRow);
    }
  }, [pageInfo]);

  const _data = React.useMemo(() => {
    return data;
  }, [data]);

  const _columns = React.useMemo(() => {
    let c = columns;

    if (c) {
      c.map((row) => {
        const { viewId, Cell, viewProps } = row;

        if (viewId && DecoratorsIn[viewId] && !Cell) {
          row.Cell = DecoratorsIn[viewId];
        }
      });
    }

    if (c || isAutoColumns === false) {
    } else {
      c = makeColumnsFromData(_data, c);
    }

    // HeaderId 가 존재하는 경우 message 로 변경
    c.map((row) => {
      if (row.HeaderId) {
        row.Header = <FormattedMessageDynamic id={row.HeaderId} />;
      }
    });

    return c;
  }, [_data]);

  if (!_data || _data.constructor != Array) {
    return <Result status="403" title="데이터가 없습니다." />;
  }

  const tableInstance = useTable(
    { columns: _columns, data: _data, defaultColumn },
    usePagination,
    useFlexLayout,
    // useBlockLayout,
    useResizeColumns
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setPageSize,
    state: { pageIndex, pageSize }
  } = tableInstance;

  return (
    // apply the table props
    <Container style={style}>
      <div>
        <div {...getTableProps()} className="table">
          <div>
            {headerGroups.map((headerGroup) => (
              <div {...headerGroup.getHeaderGroupProps()} className="row-header row tr">
                {headerGroup.headers.map((column) => {
                  const headerProps = column.getHeaderProps();
                  if (column.type === 'number' || column.viewId === 'number') {
                    headerProps.style = {
                      ...headerProps.style,
                      textAlign: 'right'
                    };
                  }
                  if (column?.viewProps?.style) {
                    headerProps.style = {
                      ...headerProps.style,
                      ...column.viewProps.style
                    };
                  }
                  return (
                    <div {...headerProps} className="th">
                      {column.render('Header')}
                      {/* Use column.getResizerProps to hook up the events correctly */}
                      {column.canResize && isResizeColumn && (
                        <div
                          {...column.getResizerProps()}
                          className={`resizer ${column.isResizing ? 'isResizing' : ''}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row);
              return (
                <TableRow
                  key={row.getRowProps().key}
                  row={row}
                  onRowClick={onRowClick}
                  cellProps={cellProps}
                  confirm={confirm}
                  cancel={cancel}
                />
              );
            })}
          </div>
        </div>
      </div>
      {_pageCount ? (
        <Pagination
          onChange={(page, pageSize) => {
            setPageRow(pageSize);
            onChange({ page, pageRow: pageSize });
          }}
          total={pageInfo.totalCount}
          pageSize={_pageRow}
          showSizeChanger={false}
          current={pageInfo.page + 1}
        />
      ) : null}
    </Container>
  );
};

export default Table;
